import {
  Component,
  inject,
  ViewContainerRef,
  ViewChild,
  ViewChildren,
  QueryList,
  ComponentRef,
  Type,
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { routes } from '../../../../app.routes';

interface RouteItem {
  path: string;
  displayName: string;
  component: any;
}

@Component({
  selector: 'app-route-aggregator',
  imports: [CommonModule],
  templateUrl: './route-aggregator.component.html',
  styleUrl: './route-aggregator.component.css',
})
export class RouteAggregatorComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild('componentContainer', { read: ViewContainerRef, static: false })
  componentContainer!: ViewContainerRef;

  @ViewChildren('gridContainer', { read: ViewContainerRef })
  gridContainers!: QueryList<ViewContainerRef>;

  selectedRoute: string = '';
  routeList: RouteItem[] = [];
  currentComponentRef: ComponentRef<any> | null = null;
  currentView: 'list' | 'grid' = 'list';
  gridComponentRefs: Map<string, ComponentRef<any>> = new Map();
  private gridInitialized = false;

  // Allowlist of routes that need dark background for white text visibility
  private darkBackgroundAllowlist: string[] = [
    'accident-location',
    'accident-report-finished',
    'claim-report-duty',
    'document-upload-finished',
    'finish',
    'insurance-information',
    'no-processing-possible',
    'personal-information-finished',
    'quick-response-info',
    'ride-information-finish',
    'token-expired',
    'what-is-covered',
  ];

  ngOnInit() {
    this.buildRouteList();

    // Listen for query parameter changes
    this.route.queryParams.subscribe(params => {
      const routeParam = params['route'];
      if (routeParam) {
        const matchingRoute = this.routeList.find(r => r.path === routeParam);
        if (matchingRoute) {
          this.selectRouteByPath(routeParam);
        }
      }
    });
  }

  ngAfterViewInit() {
    // Handle initial load - check for existing query parameter after view is initialized
    const initialRoute = this.route.snapshot.queryParams['route'];
    if (initialRoute) {
      const matchingRoute = this.routeList.find(r => r.path === initialRoute);
      if (matchingRoute) {
        this.selectRouteByPath(initialRoute);
      }
    }
  }

  ngAfterViewChecked() {
    if (
      this.currentView === 'grid' &&
      !this.gridInitialized &&
      this.gridContainers?.length > 0
    ) {
      this.initializeGridComponents();
    }
  }

  ngOnDestroy() {
    this.cleanupAllComponents();
  }

  private buildRouteList() {
    this.routeList = routes
      .filter(
        (route) =>
          route.path &&
          route.path !== '**' &&
          !route.path.includes(':') &&
          route.component
      )
      .map((route) => ({
        path: route.path as string,
        displayName: this.formatDisplayName(route.path as string),
        component: route.component,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  private formatDisplayName(path: string): string {
    if (!path) return 'Home';
    return path
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  selectRoute(route: RouteItem) {
    // Update URL query parameter instead of directly setting selectedRoute
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { route: route.path },
      queryParamsHandling: 'merge'
    });
  }

  private selectRouteByPath(routePath: string) {
    const route = this.routeList.find(r => r.path === routePath);
    if (route) {
      this.selectedRoute = route.path;
      this.loadComponent(route.component);
    }
  }

  private loadComponent(componentType: Type<any>) {
    if (this.currentComponentRef) {
      this.currentComponentRef.destroy();
    }

    if (!this.componentContainer) {
      console.warn(
        'Component container not available - make sure list view is active'
      );
      return;
    }

    this.componentContainer.clear();
    this.currentComponentRef =
      this.componentContainer.createComponent(componentType);
  }

  closeDebugView() {
    this.router.navigate(['']);
  }

  needsDarkBackground(): boolean {
    return this.darkBackgroundAllowlist.some((route) =>
      this.selectedRoute.includes(route)
    );
  }

  switchView(view: 'list' | 'grid') {
    if (this.currentView === view) return;

    // Clean up previous view
    if (this.currentView === 'grid') {
      this.cleanupGridComponents();
    }

    this.currentView = view;
    this.gridInitialized = false;

    // After view change, Angular will detect changes and ngAfterViewChecked will handle grid init
    this.cdr.detectChanges();
  }

  private initializeGridComponents() {
    if (this.gridInitialized) return;

    const containers = this.gridContainers.toArray();

    containers.forEach((container, index) => {
      if (index < this.routeList.length) {
        const route = this.routeList[index];
        try {
          container.clear();
          const componentRef = container.createComponent(route.component);
          this.gridComponentRefs.set(route.path, componentRef);

          // Style the component for grid preview
          const element = componentRef.location.nativeElement;
          element.style.transform = 'scale(0.25)';
          element.style.transformOrigin = 'top left';
          element.style.width = '400%';
          element.style.height = '400%';
          element.style.pointerEvents = 'none';
          element.style.position = 'absolute';
          element.style.top = '0';
          element.style.left = '0';
        } catch (error) {
          console.warn(`Failed to load component for ${route.path}:`, error);
        }
      }
    });

    this.gridInitialized = true;
  }

  private cleanupGridComponents() {
    this.gridComponentRefs.forEach((ref) => ref.destroy());
    this.gridComponentRefs.clear();
    this.gridInitialized = false;
  }

  private cleanupAllComponents() {
    this.cleanupGridComponents();
    if (this.currentComponentRef) {
      this.currentComponentRef.destroy();
    }
  }
}
